import { useQuery } from "react-query";
import { PropertyCollectionEntriesInput, getPropertyCollectionFolders, getPropertyCollectionItems } from "./propertyCollection.api";
import { collection, doc, getDoc, getDocs, getFirestore, query, where } from "firebase/firestore";
import { getCurrentUser } from "@src/auth/getCurrentUser";

import { usePropertyMember } from "../member/propertyMembers.service";
import { useEffect, useMemo } from "react";
import { useRealtimeCollectionData } from "@src/data-hooks/generic/useRealtimeCollectionData";
import { IRawPropertyCollection, TRawPropertyCollection } from "@propely/propely-typings";

export type GetPropertyCollectionEntriesInput = PropertyCollectionEntriesInput;

export const usePropertyCollectionItemsKey = (input: GetPropertyCollectionEntriesInput) => ["property-collection-items", input];
export const usePropertyCollectionFoldersKey = (input: GetPropertyCollectionEntriesInput) => ["property-collection-folders", input];

export const usePropertyCollectionItems = (input: GetPropertyCollectionEntriesInput, options?: { enabled?: boolean; refetchOnMount?: boolean }) => {
  return useQuery(usePropertyCollectionItemsKey(input), async () => getPropertyCollectionItems(input), options);
};

export const usePropertyCollectionFolders = (input: GetPropertyCollectionEntriesInput, options?: { enabled?: boolean; refetchOnMount?: boolean }) => {
  return useQuery(usePropertyCollectionFoldersKey(input), async () => getPropertyCollectionFolders(input), options);
};

export const usePropertyCollectionsKey = (propertyID: string | undefined) => ["property-collections", { propertyID }];
export const usePropertyCollectionKey = (propertyID: string, collectionID: string) => ["property-collections", { propertyID, collectionID }];

export const fetchPropertyCollection = async (propertyID: string, collectionID: string) => {
  const collection = await getDoc(doc(getFirestore(), "properties", propertyID, "collections", collectionID));
  return TRawPropertyCollection.parse({
    id: collection.id,
    ...collection.data(),
  });
};

export const usePropertyCollectionByID = (propertyID: string, userID: string) => {
  const $propertyCollection = useQuery(usePropertyCollectionKey(propertyID, userID), () => fetchPropertyCollection(propertyID, userID));
  return {
    $propertyCollection,
    propertyCollection: $propertyCollection.data,
  };
};

export const usePropertyCollections = (propertyID: string | undefined) => {
  const { userId } = getCurrentUser();
  const { propertyMember } = usePropertyMember(propertyID, userId);

  const $propertyCollections = useQuery(
    usePropertyCollectionsKey(propertyID),
    async () => {
      const roles = propertyMember?.roles ?? [];
      if (!roles.length) return [];
      if (!propertyID) return [];

      const propertyCollections = await getDocs(
        query(collection(getFirestore(), "properties", propertyID, "collections"), where("readAccess.roles", "array-contains-any", roles))
      );
      const result: IRawPropertyCollection[] = [];

      for (const member of propertyCollections.docs) {
        const parsedData = TRawPropertyCollection.safeParse({
          id: member.id,
          ...member.data(),
        });

        if (parsedData.success) {
          result.push(parsedData.data);
        }
      }
      return result;
    },
    {
      enabled: !!propertyID,
    }
  );

  // Refetch if propertyMember.roles changes
  useEffect(() => {
    $propertyCollections.refetch();
  }, [propertyMember?.roles]);

  return {
    $propertyCollections,
    propertyCollections: $propertyCollections.data ?? [],
  };
};

export const useRealtimePropertyCollections = (propertyID: string | undefined) => {
  const { userId } = getCurrentUser();
  const { propertyMember } = usePropertyMember(propertyID, userId);

  const firebaseQuery = useMemo(() => {
    if (!propertyID || !propertyMember?.roles || propertyMember.roles.length === 0) {
      return undefined;
    }

    return query(
      collection(getFirestore(), `properties/${propertyID}/collections`),
      where("readAccess.roles", "array-contains-any", propertyMember.roles)
    );
  }, [propertyID, propertyMember?.roles]);

  const { data, error, loading } = useRealtimeCollectionData(firebaseQuery, TRawPropertyCollection);

  return {
    propertyCollections: data,
    loading,
    error,
  };
};
